import { FC, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { notificationsService, type Notification, type NotificationType } from '@/services/notifications.service';

interface NotificationsListProps {
  /** 'popover' = uso no dropdown do sidebar; 'page' = uso na página dedicada /notificacoes */
  variant?: 'popover' | 'page';
  /** Chamado quando o usuário clica num link de notificação (o popover usa pra fechar) */
  onNavigate?: () => void;
}

const NOTIFICATION_STYLE: Record<NotificationType, { color: string; icon: string }> = {
  LEVEL_UP: { color: 'text-amber-400', icon: 'trending_up' },
  ACHIEVEMENT: { color: 'text-purple-400', icon: 'emoji_events' },
  TRAIL_COMPLETED: { color: 'text-emerald-400', icon: 'school' },
  MISSION_COMPLETED: { color: 'text-blue-400', icon: 'flag' },
  QUIZ_PASSED: { color: 'text-cyan-400', icon: 'quiz' },
  GAME_WON: { color: 'text-pink-400', icon: 'sports_esports' },
  STREAK_MILESTONE: { color: 'text-orange-400', icon: 'local_fire_department' },
  RANKING_CHANGE: { color: 'text-yellow-400', icon: 'leaderboard' },
  NEW_CONTENT: { color: 'text-teal-400', icon: 'new_releases' },
  SYSTEM: { color: 'text-content-muted', icon: 'info' },
};

/**
 * Lógica de fetch + renderização da lista de notificações.
 * Reutilizada entre o popover (NotificationsPanel) e a página dedicada (NotificationsPage).
 * Marca todas como lidas ao montar, igual ao comportamento anterior do popover.
 */
const NotificationsList: FC<NotificationsListProps> = ({ variant = 'popover', onNavigate }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const data = await notificationsService.getNotifications();
        setNotifications(variant === 'page' ? data : data.slice(0, 20));

        const hasUnread = data.some((n) => !n.is_read);
        if (hasUnread) {
          await notificationsService.markAllAsRead();
          setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
          window.dispatchEvent(new CustomEvent('notification:refresh'));
        }
      } catch (error) {
        console.error('Erro ao carregar notificações:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadNotifications();
  }, [variant]);

  const deleteNotification = async (id: string) => {
    try {
      await notificationsService.deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (error) {
      console.error('Erro ao deletar notificação:', error);
    }
  };

  const getStyle = (type: NotificationType) => {
    return NOTIFICATION_STYLE[type] || NOTIFICATION_STYLE.SYSTEM;
  };

  const formatTimestamp = (dateStr: string) => {
    const now = new Date();
    const diff = now.getTime() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return 'agora';
    if (minutes < 60) return `${minutes}m atrás`;
    if (hours < 24) return `${hours}h atrás`;
    return `${days}d atrás`;
  };

  const emptyPadding = variant === 'page' ? 'py-20' : 'py-12';

  if (isLoading) {
    return (
      <div className={`flex flex-col items-center justify-center ${emptyPadding} text-center px-4`}>
        <span className="material-symbols-outlined text-4xl text-content-muted animate-pulse mb-2">
          notifications
        </span>
        <p className="text-content-secondary text-sm">Carregando notificações...</p>
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center ${emptyPadding} text-center px-4`}>
        <span className="material-symbols-outlined text-6xl text-content-muted mb-3">notifications_off</span>
        <p className="text-content-secondary text-sm">Nenhuma notificação por enquanto</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-theme-border">
      {notifications.map((notification) => {
        const style = getStyle(notification.type);
        return (
          <div key={notification.id} className="p-4 hover:bg-surface-hover transition-colors">
            <div className="flex gap-3">
              <span className={`material-symbols-outlined text-2xl ${style.color} shrink-0`}>
                {notification.icon || style.icon}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h4 className="text-content-primary font-semibold text-sm">{notification.title}</h4>
                </div>
                <p className="text-content-secondary text-xs mb-2 leading-relaxed">{notification.message}</p>
                <div className="flex items-center justify-between">
                  <span className="text-content-muted text-2xs">
                    {formatTimestamp(notification.created_at)}
                  </span>
                  <div className="flex items-center gap-2">
                    {notification.action_url && (
                      <Link
                        to={notification.action_url}
                        onClick={() => onNavigate?.()}
                        className="text-accent hover:text-accent-hover text-xs font-medium transition-colors"
                      >
                        Ver
                      </Link>
                    )}
                    <button
                      onClick={() => deleteNotification(notification.id)}
                      className="text-content-muted hover:text-rose-500 text-xs transition-colors"
                      title="Excluir"
                    >
                      <span className="material-symbols-outlined text-sm">close</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default NotificationsList;
